import {
    differenceInMonths,
    differenceInDays as differenceInDaysFns,
    differenceInCalendarDays as differenceInCalendarDaysFns,
    isToday as isTodayFns,
    isAfter as isAfterFns,
    isBefore as isBeforeFns,
    addDays as addDaysFns,
    parse as parseFns,
    format as formatFns,
    startOfWeek,
    endOfDay as endOfDayFns,
    startOfDay as startOfDayFns,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfYear,
    endOfYear,
    parseISO,
    isMatch,
} from 'date-fns';
import isWithinIntervalFns from 'date-fns/isWithinInterval';
import enUS from 'date-fns/locale/en-US';
import { LogService } from '@services';
import { dateConstants } from './constants';

const locales = { en: enUS };
const getLocal = () => locales.en;

const dateUtils = {
    isValid: (date) => {
        return date.getTime() === date.getTime();
    },
    parseDateString: (value) => {
        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            return value;
        }

        if (value.includes('T')) {
            return new Date(value);
        }

        return dateUtils.parseString(value);
    },
    parseString: (value, format = dateConstants.FORMAT.API_DATE) => {
        if (value instanceof Date) {
            return value;
        }
        try {
            return parseFns(value, format, new Date());
        } catch (e) {
            if (process.env.NODE_ENV !== 'production') {
                LogService.warn(`Invalid date to process ${value} with format ${format}.`);
            }
            return null;
        }
    },
    parseISO: (value) => {
        return value instanceof Date ? value : parseISO(value);
    },
    format: (value, format, language) => {
        try {
            language || LogService.debug(`Language is missing.`);

            return formatFns(value, format, { locale: getLocal(language || 'en') });
        } catch (e) {
            if (process.env.NODE_ENV !== 'production') {
                LogService.warn(`Invalid date to process ${value} with format ${format}.`);
            }
            return '';
        }
    },
    formatQueryParam: (value, format = dateConstants.FORMAT.API_DATE) => {
        return formatFns(dateUtils.parseString(value, format), format);
    },
    formatQueryParamUTC(value) {
        return dateUtils.parseString(value, dateConstants.FORMAT.API_DATE).toISOString();
    },
    formatQueryParamDateBeginDayUTC: (value) => {
        const date = startOfDayFns(dateUtils.parseString(value, dateConstants.FORMAT.API_DATE));

        return date.toISOString();
    },
    formatQueryParamDateEndDayUTC: (value) => {
        const date = endOfDayFns(dateUtils.parseString(value, dateConstants.FORMAT.API_DATE));

        return date.toISOString();
    },
    getStartOfDay(value) {
        return startOfDayFns(value);
    },
    getEndOfDay(value) {
        return endOfDayFns(value);
    },
    getTomorrowStartOfDayForDate(date) {
        return startOfDayFns(addDaysFns(date, 1));
    },
    isDateAfterTomorrowStartDay(date) {
        return isAfterFns(date, dateUtils.getTomorrowStartOfDayForDate(new Date()));
    },
    isToday(date) {
        return isTodayFns(date);
    },
    isWithinInterval(date, startDate, endDate) {
        try {
            return isWithinIntervalFns(date, { start: startDate, end: endDate });
        } catch (e) {
            if (process.env.NODE_ENV !== 'production') {
                LogService.warn(
                    `Invalid interval: ${JSON.stringify(date)} ${JSON.stringify(startDate)} ${JSON.stringify(endDate)}`
                );
            }
            return false;
        }
    },
    getDifferenceInMonth(dateStart, dateEnd) {
        return Math.abs(differenceInMonths(dateStart, dateEnd));
    },
    getDateStringWithoutUTC(date) {
        let alteredDate = date.substring(0, 10).split('-');
        return new Date(alteredDate[0], alteredDate[1] - 1, alteredDate[2]);
    },
    getFormatByAggregateLevel: (dateRangeStart, dateRangeEnd) => {
        let difference = dateUtils.getDifferenceInMonth(dateRangeStart, dateRangeEnd);

        if (
            difference >= dateConstants.AGGREGATE_LEVELS.DAY.FROM &&
            difference <= dateConstants.AGGREGATE_LEVELS.DAY.TO
        ) {
            return dateConstants.FORMAT.DATE_SHORT;
        }
        if (
            difference > dateConstants.AGGREGATE_LEVELS.MONTH.FROM &&
            difference <= dateConstants.AGGREGATE_LEVELS.MONTH.TO
        ) {
            return dateConstants.FORMAT.UI_DATE_MONTH_YEAR;
        }
        if (
            difference > dateConstants.AGGREGATE_LEVELS.QUARTER.FROM &&
            difference <= dateConstants.AGGREGATE_LEVELS.QUARTER.TO
        ) {
            return dateConstants.FORMAT.UI_DATE_QUARTER_YEAR;
        }
        if (difference > dateConstants.AGGREGATE_LEVELS.YEAR.FROM) {
            return dateConstants.FORMAT.UI_DATE_YEAR;
        }

        return dateConstants.FORMAT.UI_DATE;
    },
    getDateToday() {
        return new Date();
    },
    // Week start on Sunday - Sunday starts on 0 that is why we add 1
    getLastFourWeeks() {
        return this.getPriorDateByDayNr(21 + this.getDateYesterday().getDay() + 1);
    },
    getStartOfLastThreeMonths() {
        let date = new Date();
        return new Date(date.getFullYear(), date.getMonth() - 2, 1);
    },
    getStartOfLastTwelveMonths() {
        let date = new Date();
        return new Date(date.getFullYear(), date.getMonth() - 11, 1);
    },
    getDateYesterday() {
        return this.getPriorDateByDayNr(1);
    },
    getDateStartOfThisWeek() {
        return startOfWeek(new Date());
    },
    getDateStartOfLastWeek() {
        return this.getPriorDateByDayNr(7, this.getDateStartOfThisWeek());
    },
    getDateEndOfLastWeek() {
        return this.getPriorDateByDayNr(1, this.getDateStartOfThisWeek());
    },
    getDateEndOfThisWeek() {
        return endOfWeek(new Date());
    },
    getDateStartOfThisMonth() {
        return startOfMonth(new Date());
    },
    getDateEndOfThisMonth() {
        return endOfMonth(new Date());
    },
    getDateStartOfLastMonth() {
        let date = new Date();
        return new Date(date.getFullYear(), date.getMonth() - 1, 1);
    },
    getDateEndOfLastMonth() {
        let date = new Date();
        date.setDate(1);
        date.setHours(-1);
        return date;
    },
    getDateStartOfThisYear() {
        return startOfYear(new Date());
    },
    getDateEndOfThisYear() {
        return endOfYear(new Date());
    },
    getDateStartOfLastYear() {
        return new Date(new Date().getFullYear() - 1, 0, 1);
    },
    getDateEndOfLastYear() {
        return new Date(new Date().getFullYear() - 1, 11, 31);
    },
    getDateStartOfThisQuarter() {
        var now = new Date();
        var quarter = Math.floor(now.getMonth() / 3);
        return new Date(now.getFullYear(), quarter * 3, 1);
    },
    getDateEndOfThisQuarter() {
        let now = new Date();
        let quarter = Math.floor(now.getMonth() / 3);
        let firstDate = new Date(now.getFullYear(), quarter * 3, 1);
        return new Date(firstDate.getFullYear(), firstDate.getMonth() + 3, 0);
    },
    getDateStartOfPreviousQuarter() {
        let now = new Date();
        let quarter = Math.floor(now.getMonth() / 3);
        return new Date(now.getFullYear(), quarter * 3 - 3, 1);
    },
    getDateEndOfPreviousQuarter() {
        let now = new Date();
        let quarter = Math.floor(now.getMonth() / 3);
        let firstDate = new Date(now.getFullYear(), quarter * 3 - 3, 1);
        return new Date(firstDate.getFullYear(), firstDate.getMonth() + 3, 0);
    },
    getPriorDateByDayNr(priorDayNr = 0, date = new Date()) {
        return new Date(date.getTime() - priorDayNr * 24 * 60 * 60 * 1000);
    },
    getPriorDateByMonthNr(priorMonthNr = 0) {
        const today = new Date();
        return new Date(today.setMonth(today.getMonth() - priorMonthNr));
    },
    isAfter(day1, day2) {
        return isAfterFns(day1, day2);
    },
    isBefore(day1, day2) {
        return isBeforeFns(day1, day2);
    },
    addDays(day, days) {
        return addDaysFns(day, days);
    },
    millisecondsToDays(milliseconds) {
        return Math.ceil(milliseconds / (1000 * 3600 * 24));
    },
    differenceInMilliseconds: (dateStart, dateEnd) => {
        return dateStart.getTime() - dateEnd.getTime();
    },
    differenceInDays: (dateStart, dateEnd) => {
        return differenceInDaysFns(dateStart, dateEnd);
    },
    differenceInCalendarDays: (dateStart, dateEnd) => {
        return differenceInCalendarDaysFns(dateStart, dateEnd);
    },
    calendarDaysToDate: (date) => {
        return dateUtils.differenceInCalendarDays(date, new Date());
    },
    formatAMPM: (date, displayMinutes) => {
        let hours = date.getHours();
        let minutes = date.getMinutes();
        let ampm = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        minutes = minutes < 10 ? '0' + minutes : minutes;
        const strTime = hours + (displayMinutes ? ':' + minutes + ' ' : ' ') + ampm;
        return strTime;
    },
    dateTimeToEasternTimeFormat(date) {
        const parsedDate = dateUtils.parseDateString(date);

        if (!parsedDate) {
            return '';
        }

        const options = {
            timeZone: 'America/New_York',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
        };
        const easternTime = new Date(new Intl.DateTimeFormat(getLocal, options).format(parsedDate));

        return dateUtils.format(easternTime, dateConstants.FORMAT.DATE_TIME_SHORT, getLocal);
    },
    /**
     * Calculates the time zone offset in milliseconds between the specified time zone and UTC
     * for a given date.
     *
     * @param {string} timeZone - The IANA time zone identifier (e.g., "America/New_York", "UTC").
     * @param {Date} date - The date for which to calculate the time zone offset.
     * @returns {number} The time zone offset in milliseconds.
     *
     * @example
     * const offset = getTimeZoneOffsetInMs("America/New_York", new Date("2025-09-10T12:00:00Z"));
     * console.log(offset); // e.g., -14400000 for EDT (UTC-4)
     */
    getTimeZoneOffsetInMs(timeZone, date) {
        const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
        const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));
        return tzDate.getTime() - utcDate.getTime();
    },
    /**
     * Builds a UTC `Date` object that corresponds to the specified local date and time
     * in a given IANA time zone.
     *
     * This function ensures that the returned `Date` object represents the exact intended
     * local time in the specified time zone, but in UTC.
     *
     * @param {string} date - Accepts ISO date string (e.g., "2025-09-10" or "2025-09-10T00:00:00Z")
     * @param {number} hour - The hour component (0–23) of the local time.
     * @param {number} minute - The minute component (0–59) of the local time.
     * @param {string} timeZone - The IANA time zone identifier (e.g., "America/New_York").
     * @returns {Date} A `Date` object in UTC that represents the intended local time in the specified time zone.
     *
     * @example
     * const date = buildZonedDateInUTC("2025-09-10", 14, 30, "Europe/London");
     * console.log(date.toISOString()); // Will print the UTC time corresponding to 14:30 in London on that date
     */
    buildZonedDateInUTC(date, hour, minute, timeZone) {
        const dateObj = new Date(date);
        if (!dateObj || isNaN(dateObj)) return null;
       
        const year = dateObj.getFullYear();
        const month = dateObj.getMonth();
        const day = dateObj.getDate();

        const localReference = new Date(Date.UTC(year, month, day, hour, minute, 0, 0));
        const offsetMs = this.getTimeZoneOffsetInMs(timeZone, localReference);
        const adjustedUtcDate = new Date(localReference.getTime() - offsetMs);
        return adjustedUtcDate;
    },
    /**
     * Returns the time zone abbreviation (e.g., "EDT", "PST") for a given time zone and date.
     *
     * @param {string} timeZone - An IANA time zone name (e.g., "America/New_York").
     * @returns {string|null} The time zone abbreviation, or null if no time zone is provided.
     * @example
     * Returns: "EDT"
     * getTimeZoneAbbreviation('America/New_York');
    */
    getTimeZoneAbbreviation(timeZone) {
        if(!timeZone) return null;

        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            timeZoneName: 'short'
        });
        const parts = formatter.formatToParts(new Date());

        return parts.find(part => part.type === 'timeZoneName')?.value;
    },
    /**
     * Formats a given date string using a format and appends the time zone abbreviation if provided.
     *
     * @param {string} date - An ISO-formatted date string.
     * @param {string} format - A `date-fns` format string.
     * @param {string} [timeZone] - Optional IANA time zone name (e.g., "America/Los_Angeles").
     * @returns {string} The formatted date string, with time zone abbreviation if available.
     * @example
     *  Assuming dateConstants.FORMAT.PERIOD_FULL_TIME = "yyyy-MM-dd HH:mm"
     *  Returns: "2023-09-25 08:00, EDT"
     *  formatDateWithTimeZone('2023-09-25T12:00:00Z', 'yyyy-MM-dd HH:mm', 'America/New_York
    */
    formatDateWithTimeZone(date, format, timeZone) {
        const parsedDate = dateUtils.parseISO(date);
        const formatted = this.format(parsedDate, format);
        const abbr = this.getTimeZoneAbbreviation(timeZone);
        return abbr ? `${formatted}, ${abbr}` : formatted;
    },
    /**
     * Checks if a given date string only contains a date (no time).
     *
     * @param {string} dateString - A string to check, expected to be in ISO format.
     * @returns {boolean} True if the string matches the "yyyy-MM-dd" format; false otherwise.
     * @example
     * Returns: true
     * isDateOnly('2023-09-25');
     *
     * Returns: false
     * isDateOnly('2023-09-25T12:00:00Z');
     */
    isDateOnly(dateString) {
        return isMatch(dateString, dateConstants.FORMAT.API_DATE);
    }
};

export default dateUtils;
